import { useState, useEffect } from 'react';
import api from '../api';

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [form, setForm] = useState({ imie: '', email: '', haslo: '', rola: 'PH' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await api.get('/users');
      setUsers(res.data);
    } catch (err) {
      setError('Błąd pobierania użytkowników');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingUser(null);
    setForm({ imie: '', email: '', haslo: '', rola: 'PH' });
    setError('');
    setShowForm(true);
  };

  const openEdit = (user) => {
    setEditingUser(user);
    setForm({ imie: user.imie, email: user.email, haslo: '', rola: user.rola });
    setError('');
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingUser(null);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const data = { ...form };
      if (editingUser && !data.haslo) delete data.haslo;

      if (editingUser) {
        await api.put(`/users/${editingUser.id}`, data);
      } else {
        await api.post('/users', data);
      }
      await fetchUsers();
      closeForm();
    } catch (err) {
      setError(err.response?.data?.error || 'Błąd zapisu');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/users/${id}`);
      setDeleteConfirm(null);
      await fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Błąd usuwania użytkownika');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('pl-PL');
  };

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div>
          <h2 style={s.pageTitle}>Użytkownicy</h2>
          <p style={s.pageSubtitle}>{users.length} {users.length === 1 ? 'użytkownik' : 'użytkowników'}</p>
        </div>
        <button style={s.btnPrimary} onClick={openCreate}>+ Dodaj użytkownika</button>
      </div>

      {error && <div style={s.errorBanner}>{error}</div>}

      {loading ? (
        <div style={s.loading}>Ładowanie...</div>
      ) : users.length === 0 ? (
        <div style={s.empty}>
          <div style={s.emptyIcon}>👥</div>
          <p style={s.emptyText}>Brak użytkowników</p>
          <button style={s.btnPrimary} onClick={openCreate}>Dodaj użytkownika</button>
        </div>
      ) : (
        <div style={s.tableWrapper}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Imię</th>
                <th style={s.th}>Email</th>
                <th style={s.th}>Rola</th>
                <th style={s.th}>Data dodania</th>
                <th style={{ ...s.th, width: '120px' }}>Akcje</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => (
                <tr key={u.id} style={i % 2 === 0 ? s.trEven : s.trOdd}>
                  <td style={s.td}>
                    <div style={s.userCell}>
                      <div style={s.avatar}>{u.imie.charAt(0).toUpperCase()}</div>
                      <span style={{ fontWeight: '600' }}>{u.imie}</span>
                      {u.id === currentUser.id && <span style={s.youBadge}>Ty</span>}
                    </div>
                  </td>
                  <td style={s.td}>{u.email}</td>
                  <td style={s.td}>
                    <span style={u.rola === 'ADMIN' ? s.badgeAdmin : s.badgePH}>
                      {u.rola}
                    </span>
                  </td>
                  <td style={s.td}>{formatDate(u.created_at)}</td>
                  <td style={s.td}>
                    <div style={s.actions}>
                      <button style={s.btnEdit} onClick={() => openEdit(u)}>Edytuj</button>
                      {u.id !== currentUser.id && (
                        <button style={s.btnDelete} onClick={() => setDeleteConfirm(u)}>Usuń</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal formularza */}
      {showForm && (
        <div style={s.overlay} onClick={closeForm}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>
                {editingUser ? 'Edytuj użytkownika' : 'Nowy użytkownik'}
              </h3>
              <button style={s.closeBtn} onClick={closeForm}>✕</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div style={s.field}>
                <label style={s.label}>Imię *</label>
                <input
                  style={s.input}
                  value={form.imie}
                  onChange={e => setForm({ ...form, imie: e.target.value })}
                  required
                  placeholder="np. Jan Kowalski"
                />
              </div>
              <div style={s.field}>
                <label style={s.label}>Email *</label>
                <input
                  style={s.input}
                  type="email"
                  value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                  required
                  placeholder="jan@kastime.eu"
                />
              </div>
              <div style={s.field}>
                <label style={s.label}>
                  {editingUser ? 'Nowe hasło (zostaw puste aby nie zmieniać)' : 'Hasło *'}
                </label>
                <input
                  style={s.input}
                  type="password"
                  value={form.haslo}
                  onChange={e => setForm({ ...form, haslo: e.target.value })}
                  required={!editingUser}
                  placeholder={editingUser ? 'Zostaw puste aby nie zmieniać' : 'Minimum 6 znaków'}
                />
              </div>
              <div style={s.field}>
                <label style={s.label}>Rola *</label>
                <select
                  style={s.input}
                  value={form.rola}
                  onChange={e => setForm({ ...form, rola: e.target.value })}
                  disabled={editingUser && editingUser.id === currentUser.id}
                >
                  <option value="PH">PH — Przedstawiciel Handlowy</option>
                  <option value="ADMIN">ADMIN — Administrator</option>
                </select>
                {editingUser && editingUser.id === currentUser.id && (
                  <p style={s.hint}>Nie możesz zmienić własnej roli</p>
                )}
              </div>
              {error && <p style={s.errorText}>{error}</p>}
              <div style={s.modalFooter}>
                <button type="button" style={s.btnCancel} onClick={closeForm}>Anuluj</button>
                <button type="submit" style={s.btnPrimary} disabled={saving}>
                  {saving ? 'Zapisywanie...' : (editingUser ? 'Zapisz zmiany' : 'Dodaj użytkownika')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal potwierdzenia usunięcia */}
      {deleteConfirm && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(null)}>
          <div style={{ ...s.modal, maxWidth: '400px' }} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>Usuń użytkownika</h3>
              <button style={s.closeBtn} onClick={() => setDeleteConfirm(null)}>✕</button>
            </div>
            <p style={{ padding: '0 24px 16px', color: '#555' }}>
              Czy na pewno chcesz usunąć użytkownika <strong>{deleteConfirm.imie}</strong>?
              Tej operacji nie można cofnąć.
            </p>
            <div style={{ ...s.modalFooter, borderTop: '1px solid #f0f0f0' }}>
              <button style={s.btnCancel} onClick={() => setDeleteConfirm(null)}>Anuluj</button>
              <button style={s.btnDeleteConfirm} onClick={() => handleDelete(deleteConfirm.id)}>Usuń</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  page: { padding: '32px', color: '#e6edf3' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  pageTitle: { margin: '0 0 4px', fontSize: '22px', fontWeight: '700', color: '#e6edf3' },
  pageSubtitle: { margin: 0, color: '#8b949e', fontSize: '14px' },
  btnPrimary: { padding: '10px 20px', background: '#1890ff', color: 'white', border: 'none', borderRadius: '6px', fontSize: '14px', cursor: 'pointer', fontWeight: '500' },
  btnCancel: { padding: '10px 20px', background: '#21262d', color: '#c9d1d9', border: '1px solid #30363d', borderRadius: '6px', fontSize: '14px', cursor: 'pointer' },
  btnEdit: { padding: '5px 10px', background: '#1c2b3a', color: '#58a6ff', border: '1px solid #1f4068', borderRadius: '4px', fontSize: '12px', cursor: 'pointer', marginRight: '6px' },
  btnDelete: { padding: '5px 10px', background: '#2d1b1b', color: '#ff7875', border: '1px solid #5c2323', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' },
  btnDeleteConfirm: { padding: '10px 20px', background: '#ff4d4f', color: 'white', border: 'none', borderRadius: '6px', fontSize: '14px', cursor: 'pointer' },
  errorBanner: { background: '#2d1b1b', border: '1px solid #5c2323', color: '#ff7875', padding: '12px 16px', borderRadius: '6px', marginBottom: '16px' },
  loading: { textAlign: 'center', padding: '60px', color: '#8b949e' },
  empty: { textAlign: 'center', padding: '60px', color: '#8b949e' },
  emptyIcon: { fontSize: '48px', marginBottom: '12px' },
  emptyText: { fontSize: '16px', marginBottom: '20px' },
  tableWrapper: { background: '#161b22', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.4)', border: '1px solid #30363d' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '12px 16px', background: '#0d1117', textAlign: 'left', borderBottom: '1px solid #30363d', fontWeight: '600', fontSize: '13px', color: '#8b949e' },
  td: { padding: '12px 16px', borderBottom: '1px solid #21262d', fontSize: '14px', verticalAlign: 'middle', color: '#c9d1d9' },
  trEven: { background: '#161b22' },
  trOdd: { background: '#0d1117' },
  userCell: { display: 'flex', alignItems: 'center', gap: '10px' },
  avatar: { width: '32px', height: '32px', borderRadius: '50%', background: '#1890ff', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '14px', flexShrink: 0 },
  youBadge: { background: '#1a2e1a', color: '#52c41a', border: '1px solid #2d5a2d', borderRadius: '10px', padding: '1px 8px', fontSize: '11px' },
  badgeAdmin: { background: '#2d1f0e', color: '#ffa940', border: '1px solid #5c3d1a', borderRadius: '4px', padding: '2px 8px', fontSize: '12px', fontWeight: '600' },
  badgePH: { background: '#1c2b3a', color: '#58a6ff', border: '1px solid #1f4068', borderRadius: '4px', padding: '2px 8px', fontSize: '12px', fontWeight: '600' },
  actions: { display: 'flex' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#161b22', borderRadius: '10px', width: '100%', maxWidth: '520px', boxShadow: '0 8px 32px rgba(0,0,0,0.5)', border: '1px solid #30363d' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid #30363d' },
  modalTitle: { margin: 0, fontSize: '16px', fontWeight: '700', color: '#e6edf3' },
  closeBtn: { background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#8b949e', lineHeight: 1 },
  modalFooter: { display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '16px 24px' },
  field: { padding: '12px 24px 0' },
  label: { display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500', color: '#c9d1d9' },
  input: { width: '100%', padding: '9px 12px', border: '1px solid #30363d', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box', background: '#0d1117', color: '#c9d1d9' },
  hint: { margin: '4px 0 0', fontSize: '12px', color: '#484f58' },
  errorText: { color: '#ff7875', fontSize: '13px', margin: '8px 24px 0' },
};
